import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";

const Ctx = createContext(null);

const TOKEN_KEY = "hrzn_token";
const DESKTOP_AUTH_INTENT_KEY = "hrzn_desktop_auth_intent";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const [providers, setProviders] = useState({ google: false, github: false });

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(null);
      return null;
    }
    try {
      const { data } = await api.get("/api/auth/me");
      // /me is the source of truth for the displayed email. Always
      // overwrite — never trust an older snapshot. This is what produced
      // the "shows email A while logged in as B" bug when localStorage
      // was shared across tabs or after a token swap.
      setUser(data);
      return data;
    } catch (err) {
      if (err?.response?.status === 401) setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await fetchMe();
      try {
        const r = await api.get("/api/auth/oauth/config");
        if (!cancelled) setProviders(r.data || {});
      } catch { /* OAuth providers are optional */ }
      finally { if (!cancelled) setReady(true); }
    })();
    return () => { cancelled = true; };
  }, [fetchMe]);

  // The api layer fires hrzn:auth-expired when it strips a stale token.
  // Mirror that into UI state so we re-render as logged-out without a
  // hard reload.
  useEffect(() => {
    const onExpired = () => setUser(null);
    window.addEventListener("hrzn:auth-expired", onExpired);
    return () => window.removeEventListener("hrzn:auth-expired", onExpired);
  }, []);

  // Re-verify identity when the user comes back to the tab. Catches the
  // cross-tab "I'm logged in as someone else now" case: each tab pulls a
  // fresh /me on focus instead of trusting its stale snapshot.
  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState === "visible" && localStorage.getItem(TOKEN_KEY)) {
        fetchMe();
      }
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [fetchMe]);

  // Cross-tab login/logout sync.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== TOKEN_KEY) return;
      if (!e.newValue) setUser(null);
      else fetchMe();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [fetchMe]);

  const persist = (data) => {
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    return data.user;
  };

  const login = async (email, password) => {
    const { data } = await api.post("/api/auth/login", { email, password });
    return persist(data);
  };

  const signup = async (email, password, password_confirm, display_name) => {
    const { data } = await api.post("/api/auth/signup", { email, password, password_confirm, display_name });
    return persist(data);
  };

  const verifyCode = async (email, code) => {
    const { data } = await api.post("/api/auth/verify-email", { email, code });
    if (data?.token) localStorage.setItem(TOKEN_KEY, data.token);
    if (data?.user) setUser(data.user);
    return data;
  };

  const resendCode = async (email) => {
    const { data } = await api.post("/api/auth/resend-code", { email });
    return data;
  };

  const oauthGoogle = async (code, redirect_uri) => {
    const { data } = await api.post("/api/auth/oauth/google", { code, redirect_uri });
    return persist(data);
  };

  const oauthGithub = async (code) => {
    const { data } = await api.post("/api/auth/oauth/github", { code });
    return persist(data);
  };

  const refreshUser = async () => fetchMe();

  const createDesktopLink = async () => {
    const { data } = await api.post("/api/auth/desktop-link");
    return data;
  };

  const updateProfile = async (fields) => {
    const { data } = await api.put("/api/auth/profile", fields);
    if (data?.user) setUser(data.user);
    return data;
  };

  const linkGoogle = async (code, redirect_uri) => {
    const { data } = await api.post("/api/auth/link/google", { code, redirect_uri });
    if (data?.user) setUser(data.user);
    return data;
  };

  const linkGithub = async (code) => {
    const { data } = await api.post("/api/auth/link/github", { code });
    if (data?.user) setUser(data.user);
    return data;
  };

  const unlinkProvider = async (provider) => {
    const { data } = await api.delete(`/api/auth/link/${provider}`);
    if (data?.user) setUser(data.user);
    return data;
  };

  const setPassword = async (password, password_confirm) => {
    const { data } = await api.post("/api/auth/set-password", { password, password_confirm });
    if (data?.user) setUser(data.user);
    return data;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };

  return (
    <Ctx.Provider value={{
      user, ready, providers,
      login, signup, verifyCode, resendCode,
      oauthGoogle, oauthGithub, refreshUser, logout,
      createDesktopLink, updateProfile, linkGoogle, linkGithub, unlinkProvider, setPassword,
    }}>
      {children}
    </Ctx.Provider>
  );
}

export const useAuth = () => useContext(Ctx);

export const OAUTH_REDIRECTS = {
  google: () => `${window.location.origin}/auth/callback/google`,
  github: () => `${window.location.origin}/auth/callback/github`,
};

const STATE_KEY = "hrzn_oauth_state";

export function setDesktopAuthIntent() {
  sessionStorage.setItem(DESKTOP_AUTH_INTENT_KEY, "1");
}

export function consumeDesktopAuthIntent() {
  const active = sessionStorage.getItem(DESKTOP_AUTH_INTENT_KEY) === "1";
  sessionStorage.removeItem(DESKTOP_AUTH_INTENT_KEY);
  return active;
}

function makeState() {
  const buf = new Uint8Array(16);
  (window.crypto || window.msCrypto).getRandomValues(buf);
  const state = Array.from(buf, (b) => b.toString(16).padStart(2, "0")).join("");
  sessionStorage.setItem(STATE_KEY, state);
  return state;
}

export function consumeOAuthState(received) {
  const expected = sessionStorage.getItem(STATE_KEY);
  sessionStorage.removeItem(STATE_KEY);
  return Boolean(expected) && expected === received;
}

export function buildGoogleAuthUrl(clientId) {
  const p = new URLSearchParams({
    client_id: clientId,
    redirect_uri: OAUTH_REDIRECTS.google(),
    response_type: "code",
    scope: "openid email profile",
    access_type: "online",
    prompt: "select_account",
    state: makeState(),
  });
  return `https://accounts.google.com/o/oauth2/v2/auth?${p.toString()}`;
}

export function buildGithubAuthUrl(clientId) {
  const p = new URLSearchParams({
    client_id: clientId,
    redirect_uri: OAUTH_REDIRECTS.github(),
    scope: "read:user user:email",
    allow_signup: "true",
    state: makeState(),
  });
  return `https://github.com/login/oauth/authorize?${p.toString()}`;
}
